import React, { useState } from 'react';
import { Clock, Plus } from 'lucide-react';
import type { StagingHistoryEntry } from '../lib/stagingHistory';

type Props = {
  entries: StagingHistoryEntry[];
  activeId: string | null;
  onSelect: (entry: StagingHistoryEntry) => void;
  onNew: () => void;
};

function formatRelativePt(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 45) return 'agora';
  if (s < 3600) return `há ${Math.max(1, Math.round(s / 60))} min`;
  if (s < 86400) return `há ${Math.max(1, Math.round(s / 3600))} h`;
  const d = Math.round(s / 86400);
  return d <= 1 ? 'ontem' : `há ${d} dias`;
}

export default function StagingHistoryRail({ entries, activeId, onSelect, onNew }: Props) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hovered = hoveredId ? entries.find((e) => e.id === hoveredId) : null;
  const isBlank = !activeId;

  return (
    <aside className="staging-history-rail" aria-label="Histórico de gerações do staging">
      <button
        type="button"
        className={`staging-history-rail__new${isBlank ? ' is-active' : ''}`}
        title="Novo staging — limpa imagens, prompt, tags e storyboard"
        aria-label="Novo staging, limpar tudo para gerar de novo"
        onClick={onNew}
      >
        <Plus size={13} strokeWidth={2.5} />
      </button>
      {entries.length > 0 && (
        <>
          <div className="staging-history-rail__icon" title="Histórico — clique numa linha para voltar no tempo">
            <Clock size={13} />
          </div>
          <div className="staging-history-rail__track">
            {entries.map((entry, idx) => {
              const active = entry.id === activeId;
              const hoveredTick = entry.id === hoveredId;
              const activeTags = entry.tags.filter((t) => t.active).length;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={`staging-history-rail__tick${active ? ' is-active' : ''}${hoveredTick ? ' is-hover' : ''}`}
                  aria-label={`Voltar à geração ${formatRelativePt(entry.createdAt)}: ${entry.prompt.slice(0, 80)}`}
                  title={entry.prompt.slice(0, 120)}
                  onMouseEnter={() => setHoveredId(entry.id)}
                  onMouseLeave={() => setHoveredId((cur) => (cur === entry.id ? null : cur))}
                  onFocus={() => setHoveredId(entry.id)}
                  onBlur={() => setHoveredId((cur) => (cur === entry.id ? null : cur))}
                  onClick={() => onSelect(entry)}
                >
                  <span className="staging-history-rail__line" />
                  {hoveredTick && hovered && (
                    <span className="staging-history-rail__tip" role="tooltip">
                      <span className="staging-history-rail__thumbs">
                        {hovered.images.slice(0, 3).map((img) => (
                          <img key={img.id} src={img.image_b64} alt="" />
                        ))}
                      </span>
                      <span className="staging-history-rail__meta">
                        {formatRelativePt(hovered.createdAt)}
                        {idx === 0 ? ' · atual' : ''}
                      </span>
                      <span className="staging-history-rail__prompt">{hovered.prompt}</span>
                      <span className="staging-history-rail__meta">
                        {hovered.storyboard.length} fase{hovered.storyboard.length === 1 ? '' : 's'}
                        {activeTags > 0 ? ` · ${activeTags} tag${activeTags === 1 ? '' : 's'}` : ''}
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </aside>
  );
}
